import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, DOCUMENT } from "@angular/core";
import { Router } from "@angular/router";
import { ConfigService, OAuthService } from "common";
import { AppSettings } from "projects/backoffice/src/app.settings";
import { EMPTY, interval, Observable, of } from "rxjs";
import { catchError, finalize, first, map } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class BOAuthService extends OAuthService 
{
  constructor(
    http: HttpClient, 
    router: Router, 
    configService: ConfigService, 
    private settings: AppSettings,
    @Inject(DOCUMENT) private document: Document) {
        super(configService, router, http);
    }

  get isFirstLogin(): boolean {
    return !sessionStorage.token;
  }

  private authTokenRefresh$: Observable<string> | null = null;

  override refreshAuthToken(): Observable<string> {
    const oauthUrl = this.getOAuthUrl();

    if(this.isFirstLogin) {
      this.document.location.href = oauthUrl;
      return EMPTY;
    }

    if (this.authTokenRefresh$) {
      return this.authTokenRefresh$;
    }

    const popup = window.open(oauthUrl, '_blank');
    let loginCompleted = false;

    const observable$ = new Observable<string>((observer) => {
      if (!popup) {
        observer.error('Popups are blocked');
        return;
      }

      const messageListener = (event: MessageEvent): void => {
        if (event.origin !== window.location.origin) return;

        const { clientId, accessToken, expiresIn, refreshToken } = event.data;

        if(!this.isValidAuthResponse(accessToken, clientId, expiresIn, refreshToken))
          return;

        loginCompleted = true;
        this.sessionBegin(clientId, accessToken, expiresIn, refreshToken);
        window.removeEventListener('message', messageListener);
        popup.close();
        observer.next(accessToken);
        observer.complete();
      };
      const popupMonitor$ = interval(500).pipe(
        map(() => popup.closed),
        first(closed => closed === true)
      ).subscribe(() => {
        if (!loginCompleted) {
          cleanup();
          observer.error('Re-authentication interrupted by user');
        }
      });

      const cleanup = (): void => {
        window.removeEventListener('message', messageListener);
        popupMonitor$.unsubscribe();
        if (popup && !popup.closed) {
          popup.close();
        }
      };

      window.addEventListener('message', messageListener);
      window.onbeforeunload = (): void => {
        cleanup();
      }
    }).pipe(
      catchError(() => {
        return of(null);
      }),
      finalize(() => {
        this.authTokenRefresh$ = null;
        if (popup && !popup.closed) {
          popup.close();
        }
      })
    );

    this.authTokenRefresh$ = observable$;
    return this.authTokenRefresh$;
  }

  private isValidAuthResponse(accessToken: string, clientId: string, expiresIn: string, refreshToken: string): boolean {
    return !!(accessToken && clientId && expiresIn && refreshToken);
  }

  private getOAuthUrl(): string {
    const authority = this.settings.auth.oidc.authority;
    const clientId = encodeURIComponent(this.settings.auth.oidc.clientId);
    const authenticationUrl = encodeURIComponent(this.settings.auth.oidc.redirectUrl);
    const state = encodeURIComponent(JSON.stringify({
      signInUrl: this.document.location.protocol + '//' + this.document.location.host + '/callback',
      redirectUrl: (this.document.location.pathname != '/sign-out' ? this.document.location.pathname : '/home') + this.document.location.search
    }));
    const scopes = encodeURIComponent(['openid', 'profile', 'offline_access', 'user.read.all', 'group.read.all'].join(' '));
    const oauthUrl = `${authority}oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${authenticationUrl}&state=${state}&scope=${scopes}&response_type=code&response_mode=query`;
    return oauthUrl;
  }
}
